import React, { useState } from 'react';
import {
  Box, Typography, Stack, Button, TableContainer,
  Table, TableHead, TableRow, TableCell, TableBody, Paper,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FetchWithAuth, Exercise } from '../../types';
import { API_URL } from '../../utils';
import { useLanguage } from '../../i18n/LanguageContext';
import { useExercise } from '../../contexts/ExerciseContext';
import { commonDialogStyles } from '../common/dialogStyles';
import CategoryManager from '../categories/CategoryManager';
import { AddExerciseDialog } from './AddExerciseDialog';

interface ExerciseListViewProps {
  fetchWithAuth: FetchWithAuth;
  onServiceUnavailable?: () => void;
  reloadExercises: () => Promise<void>;
}

export const ExerciseListView: React.FC<ExerciseListViewProps> = ({
  fetchWithAuth,
  onServiceUnavailable,
  reloadExercises
}) => {
  const { exercises, setExercises } = useExercise();
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [addExerciseDialogOpen, setAddExerciseDialogOpen] = useState(false);
  const [exerciseToEdit, setExerciseToEdit] = useState<Exercise | null>(null);
  const { t } = useLanguage();

  const handleExerciseAdded = (addedExercise: any) => {
    setExercises((prev) => [...prev, addedExercise]);
    setError(null);
    reloadExercises();
  };

  const handleExerciseUpdated = (updatedExercise: any) => {
    setExercises((prev) => prev.map(ex => ex.id === updatedExercise.id ? updatedExercise : ex));
    setError(null);
    reloadExercises();
  };

  const handleOpenDeleteDialog = (id: number) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId === null) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/exercises/${deleteId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setExercises((prev) => prev.filter(ex => ex.id !== deleteId));
        setDeleteDialogOpen(false);
        setDeleteId(null);
        setError(null);
        await reloadExercises();
      } else if (res.status === 409) {
        setError(t('exercise.deleteUsedError'));
        setDeleteDialogOpen(false);
        setDeleteId(null);
      } else {
        setError(t('exercise.deleteError'));
        setDeleteDialogOpen(false);
        setDeleteId(null);
      }
    } catch (err: any) {
      console.error('Error deleting exercise:', err);
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setError(t('exercise.deleteError'));
      }
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const handleOpenAddDialog = () => {
    setExerciseToEdit(null);
    setAddExerciseDialogOpen(true);
  };

  const handleOpenEditDialog = (exercise: Exercise) => {
    setExerciseToEdit(exercise);
    setAddExerciseDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setAddExerciseDialogOpen(false);
    setExerciseToEdit(null);
  };

  const groupedExercises = exercises.reduce((acc, ex) => {
    const categoryName = ex.category_name || t('exercise.uncategorized');
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(ex);
    return acc;
  }, {} as Record<string, Exercise[]>);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>{t('exercise.title')}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Button variant="contained" onClick={handleOpenAddDialog}>{t('exercise.addButton')}</Button>
        <Button variant="outlined" onClick={() => setCategoryManagerOpen(true)}>{t('exercise.manageCategories')}</Button>
        {error && <Typography color="error">{error}</Typography>}
      </Stack>

      <Dialog
        open={categoryManagerOpen}
        onClose={() => setCategoryManagerOpen(false)}
        sx={{
          ...commonDialogStyles,
          '& .MuiDialog-paper': {
            width: '90vw',
            maxWidth: 500,
            minWidth: { xs: '90vw', sm: 400, md: 500 },
            margin: { xs: 1, sm: 'auto' },
          },
        }}
      >
        <DialogTitle>{t('exercise.manageCategories')}</DialogTitle>
        <DialogContent>
          <CategoryManager fetchWithAuth={fetchWithAuth} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryManagerOpen(false)}>
            {t('exercise.close')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {Object.entries(groupedExercises).map(([categoryName, exercisesInCategory]) => (
        <Accordion key={categoryName} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{categoryName}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('exercise.name')}</TableCell>
                    <TableCell align="right">{t('exercise.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exercisesInCategory.map((ex) => (
                    <TableRow key={ex.id}>
                      <TableCell>
                        {ex.name}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" onClick={() => handleOpenEditDialog(ex)} sx={{ mr: 1 }}>{t('exercise.edit')}</Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => handleOpenDeleteDialog(ex.id)}>{t('exercise.delete')}</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}
      
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete} sx={commonDialogStyles}>
        <DialogTitle>{t('exercise.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('exercise.deleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>{t('exercise.cancel')}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">{t('exercise.delete')}</Button>
        </DialogActions>
      </Dialog>

      <AddExerciseDialog
        open={addExerciseDialogOpen}
        onClose={handleCloseDialog}
        onExerciseAdded={handleExerciseAdded}
        onExerciseUpdated={handleExerciseUpdated}
        exerciseToEdit={exerciseToEdit}
        fetchWithAuth={fetchWithAuth}
        onServiceUnavailable={onServiceUnavailable}
      />
    </Box>
  );
}; 